/**
 * ProjectPlannerCard — Iter 290zp.13z15 consolidation.
 *
 * The Planner tab on Project Detail is no longer a standalone in-place
 * draft UI. It's a window onto the project's Workbench session — the
 * single canonical brainstorm → plan → estimate flow.
 *
 * Three render states:
 *   1. No draft, no legacy plan → big "Open in Workbench" CTA. Clicking
 *      it creates a fresh workbench draft pre-linked to this project
 *      and navigates the user to it.
 *   2. Has a draft (incl. lazy-backfilled from a legacy project_plans
 *      row) → summary card showing stage, last edited, BOM item count.
 *      Buttons: Continue in Workbench · View BOM PDF · View Estimate PDF.
 *   3. Loading → small spinner.
 *
 * Notes:
 *   - The old "Generate picking list" button is gone. Picking is driven
 *     by IQ Retail-imported sales orders, not by planning output.
 *     (See ProjectPickingCard / PickingHub.)
 *   - The legacy quick-draft form (description textarea → instant AI
 *     plan) is also gone. The same conversation now happens inside the
 *     workbench brainstorm stage.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList, Sparkles, Loader2, FileText, ArrowRight, Receipt,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";

const STAGE_PILL = {
  brainstorm: { label: "Brainstorm", c: "#A78BFA" },
  plan:       { label: "Plan",       c: "#1FB6C1" },
  estimate:   { label: "Estimate",   c: "#10B981" },
};

export default function ProjectPlannerCard({ projectId, customerId }) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Fetch the project-linked workbench draft (with lazy-backfill from
  // a legacy plan if applicable). 404 means no session yet — we render
  // the "Start" state.
  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      if (!projectId) { setLoading(false); return; }
      setLoading(true);
      try {
        const { data } = await api.get(`/workbench/drafts/by-project/${projectId}`);
        if (!cancelled) setDraft(data);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setDraft(null);
        } else {
          toast.error(err?.response?.data?.detail || "Couldn't load workbench session.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [projectId]);

  // Start a brand-new workbench session tied to this project.
  const startSession = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/workbench/drafts", {
        customer_id: customerId,
        project_id: projectId,
      });
      navigate(`/projects/workbench/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start workbench session.");
    } finally {
      setBusy(false);
    }
  };

  const continueSession = () => {
    if (!draft?.id) return;
    navigate(`/projects/workbench/${draft.id}`);
  };

  // Build absolute URLs for the BOM / Estimate PDF endpoints. We pass
  // the same Authorization token via cookie-less direct navigation —
  // when the JWT is attached server-side (cookie) this works; when it
  // isn't, we open the route in the SPA which fetches with the bearer
  // header. Easiest UX: open in new tab so the user keeps Planner open.
  const pdfHref = useMemo(() => {
    if (!draft?.id) return null;
    const apiBase = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
    return {
      bom: `${apiBase}/api/workbench/drafts/${draft.id}/bom-pdf?download=false`,
      estimate: `${apiBase}/api/workbench/drafts/${draft.id}/estimate-pdf?download=false`,
    };
  }, [draft?.id]);

  const openPdf = async (kind) => {
    if (!draft?.id) return;
    // We can't put a Bearer header on a window.open(), so fetch as blob
    // and present via blob URL. Same trick used by Workbench's "View
    // PDF" buttons elsewhere in the codebase.
    setBusy(true);
    try {
      const resp = await api.get(
        `/workbench/drafts/${draft.id}/${kind === "bom" ? "bom-pdf" : "estimate-pdf"}`,
        { responseType: "blob", params: { download: false } },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      // Revoke after 60s — Chrome holds the doc reference long enough.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Couldn't load ${kind} PDF.`);
    } finally {
      setBusy(false);
    }
  };

  const stagePill = STAGE_PILL[draft?.current_stage] || STAGE_PILL.brainstorm;
  const bomCount = (draft?.bom || []).filter((it) => it?.status !== "removed").length;
  const lastUpdated = draft?.updated_at
    ? new Date(draft.updated_at).toLocaleString(undefined, {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
      })
    : null;

  return (
    <div className="hv-card p-4" data-testid="project-planner-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-[#1FB6C1]" />
          <h3 className="font-display text-base font-bold">Planning</h3>
          {draft && (
            <span
              className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold"
              style={{ background: `${stagePill.c}22`, color: stagePill.c }}
              data-testid="planner-stage-pill"
            >
              {stagePill.label}
            </span>
          )}
        </div>
        {draft && (
          <button
            type="button"
            onClick={continueSession}
            className="hv-btn hv-btn-primary text-[11px]"
            data-testid="planner-continue-workbench"
          >
            Continue in Workbench <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {loading && (
        <div className="text-xs text-[#647087]" data-testid="planner-loading">
          <Loader2 className="w-3 h-3 inline animate-spin" /> Loading session…
        </div>
      )}

      {!loading && !draft && (
        <div
          className="text-center py-6 space-y-3 border border-dashed border-white/10 rounded-md"
          data-testid="planner-empty"
        >
          <Sparkles className="w-6 h-6 mx-auto text-[#1FB6C1]" />
          <div className="text-sm text-[#a0aabf] max-w-md mx-auto">
            Brainstorm, plan and estimate this system in the Workbench. The
            session stays linked to this project — BOM and Estimate PDFs come
            straight back here.
          </div>
          <button
            type="button"
            onClick={startSession}
            disabled={busy}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="planner-start-workbench"
          >
            {busy
              ? <><Loader2 className="w-3 h-3 animate-spin" /> Starting…</>
              : <><Sparkles className="w-3 h-3" /> Open in Workbench</>}
          </button>
        </div>
      )}

      {!loading && draft && (
        <div className="space-y-2.5" data-testid="planner-session-summary">
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            <div className="rounded-md border border-white/5 bg-white/[0.02] px-2.5 py-2">
              <div className="text-[9px] uppercase tracking-wider text-[#647087]">BOM items</div>
              <div className="font-mono text-base font-bold mt-0.5" data-testid="planner-bom-count">
                {bomCount}
              </div>
            </div>
            <div className="rounded-md border border-white/5 bg-white/[0.02] px-2.5 py-2">
              <div className="text-[9px] uppercase tracking-wider text-[#647087]">Stage</div>
              <div className="text-sm font-bold mt-0.5">{stagePill.label}</div>
            </div>
            <div className="rounded-md border border-white/5 bg-white/[0.02] px-2.5 py-2">
              <div className="text-[9px] uppercase tracking-wider text-[#647087]">Last edited</div>
              <div className="text-[11px] mt-0.5">{lastUpdated || "—"}</div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <button
              type="button"
              onClick={() => openPdf("bom")}
              disabled={busy || bomCount === 0}
              className="hv-btn hv-btn-ghost text-[11px] disabled:opacity-50"
              data-testid="planner-bom-pdf"
              title={bomCount === 0
                ? "Add items in the Workbench to generate the BOM."
                : "Open BOM PDF (use this to create the IQ Retail sales order)"}
            >
              <FileText className="w-3 h-3" /> BOM PDF
            </button>
            <button
              type="button"
              onClick={() => openPdf("estimate")}
              disabled={busy || draft.current_stage !== "estimate"}
              className="hv-btn hv-btn-ghost text-[11px] disabled:opacity-50"
              data-testid="planner-estimate-pdf"
              title={draft.current_stage !== "estimate"
                ? "Promote the draft to Estimate stage before exporting."
                : "Open Estimate PDF (priced, send to customer)"}
            >
              <Receipt className="w-3 h-3" /> Estimate PDF
            </button>
          </div>

          {draft.backfilled_from_legacy_plan && (
            <div
              className="text-[10px] text-amber-300/80 px-2.5 py-1.5 rounded border border-amber-500/20 bg-amber-500/[0.04]"
              data-testid="planner-legacy-notice"
            >
              This project&apos;s planning was migrated from the legacy planner.
              Open Workbench to continue refining brainstorm / plan / estimate.
            </div>
          )}
        </div>
      )}

      {/* Hidden anchors used by some tests — preserves previous data-testids */}
      <span className="hidden" data-testid="planner-status-pill" aria-hidden />
    </div>
  );
}
